import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import { Typography, withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import Dialog from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Dialog';
import DialogContent from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogContent';
import {
  CardElement as StripeCardElement,
  injectStripe,
} from 'react-stripe-elements';
import moment from 'moment';
import { withUserContextAdapter } from 'Context/UserContext';
import { withHouseholdContextAdapter } from 'Context/SimpleHouseholdContext';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Button from 'Components/UI/BehavioralFormElement/Button/Solid/Green';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import TextInput from 'Components/UI/BehavioralFormElement/Text/Input';
import DialogTitle from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogTitle';

const ErrorTypography = withStyles(theme => ({
  root: {
    color: theme.palette.error.main,
  },
}))(Typography);

const styles = theme => ({
  dialogPaper: {
    width: '100%',
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
  },
});

class PaymentModal extends Component {
  constructor(props) {
    super(props);

    this.onCompleteSignUp = this.onCompleteSignUp.bind(this);
    this.onReportError = this.onReportError.bind(this);
    this.onFieldUpdated = this.onFieldUpdated.bind(this);
    this.updateState = this.updateState.bind(this);

    const { SimpleHouseholdContext } = this.props;
    const { householdResource } = SimpleHouseholdContext;

    this.state = {
      isOpen: true,
      name: `${householdResource.attr('primaryClientFirstName')} ${householdResource.attr('primaryClientLastName')}`,
    };
  }

  onCompleteSignUp() {
    const {
      stripe: { createToken },
      onSuccess,
      onFailure,
      householdId,
    } = this.props;
    const { postPaymentSource } = this.context;
    const { onReportError } = this;
    const { name } = this.state;
    createToken({ name })
      .then(({ token: { id: token }, error }) => {
        if (typeof error === 'object' && 'message' in error) {
          onReportError(error, error.message);
        } else {
          this.setState(
            {
              loading: true,
            },
            () => {
              postPaymentSource(householdId, {
                attributes: {
                  token,
                },
              })
                .then(() => onSuccess())
                .catch((e) => {
                  onFailure(e);
                  this.setState({
                    loading: false,
                  });
                });
            },
          );
        }
      })
      .catch(({ error }) => {
        if (typeof error === 'object' && 'message' in error) {
          onReportError(error, error.message);
        }
      });
  }

  onReportError(err, message) {
    displayErrorMessage(err, message);
    this.setState({
      loading: false,
    });
  }

  onFieldUpdated(name, value) {
    this.setState({
      [name]: value,
    });
  }

  updateState(name, value) {
    this.setState({
      [name]: value,
    });
  }

  render() {
    const {
      updateState,
      onCompleteSignUp,
      props: {
        classes,
        close,
        errorMessage,
        cardLastFour,
        paymentAttemptedOn,
        paymentAmountRemaining,
      },
      state: { name, isOpen, loading },
    } = this;
    return (
      <Fragment>
        {loading && <LoadingOverlay />}
        <Dialog
          classes={{
            root: classNames('PaymentModal', classes.dialogRoot),
            paper: classes.dialogPaper,
            content: classes.dialogContent,
          }}
          open={isOpen}
          onClose={close}
          size="lg"
        >
          <DialogTitle>
            {'Add card information'}
          </DialogTitle>
          <DialogContent
            className={errorMessage !== '' ? 'modal-body-with-error' : ''}
          >
            {errorMessage !== '' && (
              <div className="error-content">
                <ErrorTypography
                  align="center"
                  variant="h4"
                  component="div"
                  gutterBottom
                >
                  Uh-oh!
                </ErrorTypography>
                <Typography gutterBottom>
                  When we tried to charge the card ending in {cardLastFour} on{' '}
                  {moment(paymentAttemptedOn).format('MMMM Do')}, we received an
                  error:
                </Typography>
                <ErrorTypography
                  align="center"
                  variant="h4"
                  component="div"
                  gutterBottom
                >
                  {errorMessage}
                </ErrorTypography>
                <Typography gutterBottom>
                  Please enter a new card number below. The client will be
                  charged ${parseInt(paymentAmountRemaining, 10) / 100}.
                </Typography>
              </div>
            )}
            <TextInput
              label="Name on Card"
              placeholder="Name on Card"
              name="name"
              value={name}
              onChange={updateState}
            />
            <StripeCardElement
              style={{ base: { fontSize: '16px', border: '1px solid black' } }}
            />
            <Button onClick={onCompleteSignUp}>
              <FontAwesome icon="lock" />
              <span>Process</span>
            </Button>
          </DialogContent>
        </Dialog>
      </Fragment>
    );
  }
}

PaymentModal.contextType = SwaggerlessContext;

PaymentModal.propTypes = {
  classes: PropTypes.object.isRequired,
  errorMessage: PropTypes.string,
  cardLastFour: PropTypes.string,
  paymentAttemptedOn: PropTypes.string,
  paymentAmountRemaining: PropTypes.string,
  stripe: PropTypes.object.isRequired,
  close: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  SimpleHouseholdContext: PropTypes.object.isRequired,
  householdId: PropTypes.string.isRequired,
};

PaymentModal.defaultProps = {
  errorMessage: '',
  cardLastFour: '',
  paymentAttemptedOn: '',
  paymentAmountRemaining: '',
  close: () => {},
};

export default withUserContextAdapter(
  withHouseholdContextAdapter(
    injectStripe(withStyles(styles)(PaymentModal)),
  ),
);
